import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config.js';

const IDLE = { status: 'idle', pictures: [], truncated: false };

/**
 * The visit of one picture, as `GET /api/pictures/:id/visit` answers it.
 *
 * Moving the highlight to another picture of the same visit does not reload it: the URL follows the
 * highlighted picture, and a visit of a hundred photos must not be fetched again at each step. A
 * picture deleted meanwhile stays known to the visit, so its id in the URL does not reload it either,
 * but `contains` says it is gone.
 *
 * @param {object} options
 * @param {?string} options.pictureId anchor of the visit, from the route
 * @param {Function} options.fetchWithAuth
 * @param {boolean} options.asClient an admin previewing the client view
 * @returns {{ status: 'idle'|'loading'|'ready'|'not_found'|'error', pictures: object[], truncated: boolean,
 *   reload: Function, updatePicture: Function, removePicture: Function, contains: (id: number) => boolean }}
 */
export const useVisit = ({ pictureId, fetchWithAuth, asClient }) => {
  const [visit, setVisit] = useState(IDLE);
  const loadedRef = useRef({ asClient: null, ids: new Set() });
  const membersRef = useRef(new Set());
  const runRef = useRef(0);

  const load = useCallback(async (id) => {
    const run = ++runRef.current;
    setVisit({ ...IDLE, status: 'loading' });

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/pictures/${id}/visit${asClient ? '?as_client=true' : ''}`, { noLogOut: true });
      if (run !== runRef.current) return;

      if (response.status === 404 || response.status === 400) {
        loadedRef.current = { asClient, ids: new Set() };
        membersRef.current = new Set();
        setVisit({ ...IDLE, status: 'not_found' });
        return;
      }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      if (run !== runRef.current) return;

      loadedRef.current = { asClient, ids: new Set(payload.pictures.map(picture => String(picture.id))) };
      membersRef.current = new Set(loadedRef.current.ids);
      setVisit({ status: 'ready', pictures: payload.pictures, truncated: !!payload.truncated });
    } catch (error) {
      if (run !== runRef.current) return;
      console.error('Error fetching visit:', error);
      loadedRef.current = { asClient, ids: new Set() };
      membersRef.current = new Set();
      setVisit({ ...IDLE, status: 'error' });
    }
  }, [asClient, fetchWithAuth]);

  useEffect(() => {
    if (!pictureId) {
      runRef.current++;
      loadedRef.current = { asClient: null, ids: new Set() };
      membersRef.current = new Set();
      setVisit(IDLE);
      return;
    }

    const loaded = loadedRef.current;
    if (loaded.asClient === asClient && loaded.ids.has(String(pictureId))) return;

    load(pictureId);
  }, [asClient, load, pictureId]);

  const reload = useCallback(() => { if (pictureId) load(pictureId); }, [load, pictureId]);

  const updatePicture = useCallback((id, patch) => setVisit(previous => ({
    ...previous,
    pictures: previous.pictures.map(picture => (picture.id === id ? { ...picture, ...patch } : picture)),
  })), []);

  const removePicture = useCallback((id) => {
    membersRef.current.delete(String(id));
    setVisit(previous => ({ ...previous, pictures: previous.pictures.filter(picture => picture.id !== id) }));
  }, []);

  const contains = useCallback((id) => membersRef.current.has(String(id)), []);

  return { ...visit, reload, updatePicture, removePicture, contains };
};
